import React from 'react';
import { makeStyles, createStyles } from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import { TEAL_PRIMARY } from 'src/utils/Style/Theme';
import { PlanItemStatus } from 'src/pages/AssortmentBuild/Planning.types';

const TRANSITION_MS = 480;

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: 18,
      height: 18,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      lineHeight: 1,
      overflow: 'visible',
    },
    layer: {
      position: 'absolute',
      inset: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      transformOrigin: '50% 50%',
      willChange: 'transform, opacity',
    },

    iconEnter: {
      animation: `$iconEnter ${TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`,
    },
    iconExit: {
      animation: `$iconExit ${TRANSITION_MS}ms cubic-bezier(0.55, 0.06, 0.68, 0.19) forwards`,
    },

    pendingIcon: {
      color: TEAL_PRIMARY,
      fontSize: '0.95rem',
      animation: '$pulse 1600ms ease-in-out infinite',
    },
    pendingIconStatic: {
      color: TEAL_PRIMARY,
      fontSize: '0.95rem',
    },
    spinnerIcon: {
      color: TEAL_PRIMARY,
      fontSize: '1.1rem',
      lineHeight: 1,
      animation: '$spin 900ms linear infinite',
    },
    completedIcon: {
      color: TEAL_PRIMARY,
      fontSize: '1rem',
    },
    failedIcon: {
      color: '#d32f2f',
      fontSize: '1rem',
    },

    '@keyframes iconEnter': {
      '0%': {
        opacity: 0,
        transform: 'rotate(-140deg) scale(0.45)',
      },
      '60%': {
        opacity: 1,
        transform: 'rotate(8deg) scale(1.08)',
      },
      '100%': {
        opacity: 1,
        transform: 'rotate(0deg) scale(1)',
      },
    },
    '@keyframes iconExit': {
      '0%': {
        opacity: 1,
        transform: 'rotate(0deg) scale(1)',
      },
      '100%': {
        opacity: 0,
        transform: 'rotate(120deg) scale(0.35)',
      },
    },
    '@keyframes pulse': {
      '0%': {
        opacity: 0.8,
        transform: 'scale(0.9)',
      },
      '50%': {
        opacity: 1,
        transform: 'scale(1)',
      },
      '100%': {
        opacity: 0.8,
        transform: 'scale(0.9)',
      },
    },
    '@keyframes spin': {
      from: {
        transform: 'rotate(0deg)',
      },
      to: {
        transform: 'rotate(360deg)',
      },
    },
  })
);

type Props = {
  status: PlanItemStatus;
};

/** Renders a status icon based on the plan item status, made into separate component to handle transitions */
export const PlanningStatusIcon = ({ status }: Props) => {
  const styles = useStyles();
  const [prevStatus, setPrevStatus] = React.useState<PlanItemStatus | null>(null);
  const lastStatusRef = React.useRef(status);

  React.useEffect(() => {
    if (lastStatusRef.current !== status) {
      setPrevStatus(lastStatusRef.current);
      lastStatusRef.current = status;

      const timeout = window.setTimeout(() => {
        setPrevStatus(null);
      }, TRANSITION_MS);

      return () => window.clearTimeout(timeout);
    }

    return undefined;
  }, [status]);

  const renderIcon = (iconStatus: PlanItemStatus, isExiting = false) => {
    switch (iconStatus) {
      case PlanItemStatus.Processing:
        return <i className={`fa fa-spinner ${styles.spinnerIcon}`} />;

      case PlanItemStatus.Completed:
        return <CheckIcon className={styles.completedIcon} />;

      case PlanItemStatus.Failed:
        return <CloseIcon className={styles.failedIcon} />;

      case PlanItemStatus.Pending:
      default:
        return (
          <FiberManualRecordIcon
            className={isExiting ? styles.pendingIconStatic : styles.pendingIcon}
          />
        );
    }
  };

  return (
    <span className={styles.root}>
      {prevStatus !== null && prevStatus !== status && (
        <span className={`${styles.layer} ${styles.iconExit}`}>
          {renderIcon(prevStatus, true)}
        </span>
      )}

      <span className={`${styles.layer} ${styles.iconEnter}`}>
        {renderIcon(status)}
      </span>
    </span>
  );
};
